// Task 28 — Canonical (color, icon, label) per ToolStatus.
//
// Referenced from MessageBubble pills, Wave 7 PersonaContextPanel rows,
// and the TurnTelemetryFooter's status chips. Spec § 3.6 mandates
// single-source — a palette swap here moves every surface at once.
//
// Icon is load-bearing for colorblind a11y; never render a color chip
// without its icon.

export type ToolStatus =
  | 'ok'
  | 'deduped'
  | 'rate_limited'
  | 'timeout'
  | 'error'
  | 'redis_unavailable'
  | 'cached'
  | 'running'
  | 'budget_exceeded';

export interface PaletteEntry {
  color: string;       // text color class (dark/light tuned)
  bgClass: string;     // pill background + border tint
  icon: string;        // glyph rendered next to label
  label?: string;      // shown next to tool name when status !== 'ok'
}

export const STATUS_PALETTE: Record<ToolStatus, PaletteEntry> = {
  ok: {
    color: 'text-emerald-600 dark:text-emerald-400',
    bgClass: 'bg-emerald-50 dark:bg-emerald-900/20',
    icon: '●',
  },
  deduped: {
    color: 'text-slate-500 dark:text-slate-400',
    bgClass: 'bg-slate-50 dark:bg-slate-900/30',
    icon: '◐',
    label: 'deduped',
  },
  rate_limited: {
    color: 'text-amber-500 dark:text-amber-300',
    bgClass: 'bg-amber-50 dark:bg-amber-900/20',
    icon: '⏳',
    label: 'rate limited',
  },
  timeout: {
    color: 'text-amber-600 dark:text-amber-300',
    bgClass: 'bg-amber-50 dark:bg-amber-900/20',
    icon: '⏱',
    label: 'timeout',
  },
  error: {
    color: 'text-rose-500 dark:text-rose-300',
    bgClass: 'bg-rose-50 dark:bg-rose-900/20',
    icon: '✕',
    label: 'error',
  },
  redis_unavailable: {
    color: 'text-rose-400 dark:text-rose-300',
    bgClass: 'bg-rose-50 dark:bg-rose-900/20',
    icon: '🔌',
    label: 'redis down',
  },
  cached: {
    color: 'text-indigo-400 dark:text-indigo-300',
    bgClass: 'bg-indigo-50 dark:bg-indigo-900/20',
    icon: '⚡',
    label: 'cached',
  },
  running: {
    color: 'text-violet-400 dark:text-violet-300',
    bgClass: 'bg-violet-50 dark:bg-violet-900/20',
    icon: '⏳',
    label: 'running',
  },
  budget_exceeded: {
    color: 'text-rose-500 dark:text-rose-300',
    bgClass: 'bg-rose-50 dark:bg-rose-900/20',
    icon: '💰',
    label: 'budget',
  },
};

export function getPaletteEntry(status: string | undefined | null): PaletteEntry {
  const s = (status ?? 'ok') as ToolStatus;
  return STATUS_PALETTE[s] ?? STATUS_PALETTE.ok;
}
